import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  LinearProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  PlayArrow,
  Pause,
  ContentCut,
  Crop,
  VolumeOff,
  VolumeUp,
  Settings,
  Check,
} from '@mui/icons-material';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';

interface MobileVideoEditorProps {
  file: File;
  onSave: (editedFile: File, originalSize: number, finalSize: number) => void;
  onCancel: () => void;
}

interface TrimSegment {
  start: number;
  end: number;
  duration: number;
}

const MobileVideoEditor: React.FC<MobileVideoEditorProps> = ({
  file,
  onSave,
  onCancel,
}) => {
  const [ffmpeg, setFfmpeg] = useState<any>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [videoInfo, setVideoInfo] = useState<{
    duration: number;
    size: number;
    resolution: string;
  } | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [trimSegment, setTrimSegment] = useState<TrimSegment>({
    start: 0,
    end: 30,
    duration: 30,
  });
  const [timelineFrames, setTimelineFrames] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState<'start' | 'end' | 'segment' | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string>('');
  const [isMuted, setIsMuted] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Initialize FFmpeg
  useEffect(() => {
    const initFFmpeg = async () => {
      try {
        const ffmpegInstance = new FFmpeg();
        
        ffmpegInstance.on('log', ({ message }) => {
          console.log('FFmpeg Log:', message);
        });

        try {
          await ffmpegInstance.load();
          setFfmpeg(ffmpegInstance);
        } catch (defaultError) {
          await ffmpegInstance.load({
            coreURL: 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/ffmpeg-core.js',
            wasmURL: 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/ffmpeg-core.wasm',
          });
          setFfmpeg(ffmpegInstance);
        }
      } catch (err) {
        setError(`Failed to load FFmpeg: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    };

    initFFmpeg();
  }, []);

  // Load video and generate timeline frames
  useEffect(() => {
    const loadVideo = async () => {
      if (!file) return;

      const url = URL.createObjectURL(file);
      setVideoUrl(url);

      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        const duration = video.duration;
        const size = file.size;
        const resolution = `${video.videoWidth}x${video.videoHeight}`;

        setVideoInfo({ duration, size, resolution });

        // Set initial trim segment
        const initialEnd = Math.min(30, duration);
        setTrimSegment({
          start: 0,
          end: initialEnd,
          duration: initialEnd,
        });

        // Generate timeline frames
        generateTimelineFrames(video, duration);
      };

      video.src = url;
    };

    loadVideo();
  }, [file]);

  // Generate timeline preview frames
  const generateTimelineFrames = async (video: HTMLVideoElement, duration: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const frameCount = 20;
    const frames: string[] = [];

    for (let i = 0; i < frameCount; i++) {
      const time = (duration / frameCount) * i;
      video.currentTime = time;
      
      await new Promise<void>((resolve) => {
        video.onseeked = () => {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const frameDataUrl = canvas.toDataURL('image/jpeg', 0.3);
          frames.push(frameDataUrl);
          resolve();
        };
      });
    }

    setTimelineFrames(frames);
  };

  // Video playback controls
  const togglePlay = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  // Timeline drag handlers
  const handleTimelineMouseDown = (e: React.MouseEvent, type: 'start' | 'end' | 'segment') => {
    setIsDragging(true);
    setDragType(type);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !timelineRef.current || !videoInfo) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const time = percentage * videoInfo.duration;

    if (dragType === 'start') {
      const newStart = Math.min(time, trimSegment.end - 1);
      setTrimSegment(prev => ({
        ...prev,
        start: newStart,
        duration: prev.end - newStart,
      }));
    } else if (dragType === 'end') {
      const newEnd = Math.max(time, trimSegment.start + 1);
      setTrimSegment(prev => ({
        ...prev,
        end: newEnd,
        duration: newEnd - prev.start,
      }));
    } else if (dragType === 'segment') {
      const segmentWidth = trimSegment.duration / videoInfo.duration;
      const newStart = Math.max(0, Math.min(videoInfo.duration - trimSegment.duration, time - (segmentWidth * videoInfo.duration) / 2));
      const newEnd = newStart + trimSegment.duration;
      setTrimSegment({
        start: newStart,
        end: newEnd,
        duration: trimSegment.duration,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragType(null);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // Update current time during playback
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, []);

  // Process video
  const handleSave = async () => {
    if (!ffmpeg || !file || !videoInfo) return;

    setIsProcessing(true);
    setProgress(0);
    setError('');
    setStatus('Processing video...');

    try {
      // Write input file
      setStatus('Loading video...');
      await ffmpeg.writeFile('input.mp4', await fetchFile(file));

      // Set up progress monitoring
      ffmpeg.on('progress', ({ progress }: { progress: number }) => {
        setProgress(progress * 100);
        setStatus(`Processing... ${Math.round(progress * 100)}%`);
      });

      // Run trim command
      setStatus('Trimming video...');
      await ffmpeg.exec([
        '-i', 'input.mp4',
        '-ss', trimSegment.start.toString(),
        '-t', trimSegment.duration.toString(),
        '-c', 'copy',
        '-avoid_negative_ts', 'make_zero',
        'output.mp4'
      ]);

      // Read output file
      setStatus('Finalizing...');
      const data = await ffmpeg.readFile('output.mp4');
      const processedBlob = new Blob([data], { type: 'video/mp4' });
      const processedFile = new File([processedBlob], file.name.replace(/\.[^/.]+$/, '_edited.mp4'), {
        type: 'video/mp4',
      });

      const originalSize = file.size;
      const finalSize = processedFile.size;

      setStatus('Complete!');
      setProgress(100);

      // Call callback
      onSave(processedFile, originalSize, finalSize);

    } catch (err) {
      setError(`Processing failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!videoInfo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading video...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: 'background.default',
      position: 'relative'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <IconButton onClick={onCancel}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6">EDIT</Typography>
        <IconButton onClick={handleSave} disabled={isProcessing}>
          <Check />
        </IconButton>
      </Box>

      {/* Video Player */}
      <Box sx={{ 
        flex: 1, 
        position: 'relative',
        bgcolor: 'black',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <video
          ref={videoRef}
          src={videoUrl}
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'contain'
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          muted={isMuted}
        />
        
        {/* Play Button Overlay */}
        {!isPlaying && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'rgba(255,255,255,0.9)',
            borderRadius: '50%',
            width: 80,
            height: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
          onClick={togglePlay}
          >
            <PlayArrow sx={{ fontSize: 40, color: 'primary.main' }} />
          </Box>
        )}
      </Box>

      {/* Control Bar */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-around',
        p: 2,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <IconButton>
          <Settings />
        </IconButton>
        <IconButton>
          <ContentCut />
        </IconButton>
        <IconButton>
          <Crop />
        </IconButton>
        <IconButton onClick={toggleMute}>
          {isMuted ? <VolumeOff /> : <VolumeUp />}
        </IconButton>
      </Box>

      {/* Timeline */}
      <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
        <Box
          ref={timelineRef}
          sx={{
            position: 'relative',
            height: 60,
            bgcolor: 'grey.100',
            borderRadius: 1,
            overflow: 'hidden',
            cursor: isDragging ? 'grabbing' : 'grab',
            mb: 1
          }}
          onMouseDown={(e) => handleTimelineMouseDown(e, 'segment')}
        >
          {/* Timeline Frames */}
          <Box sx={{ display: 'flex', height: '100%' }}>
            {timelineFrames.map((frame, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  backgroundImage: `url(${frame})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderRight: '1px solid rgba(255,255,255,0.3)',
                }}
              />
            ))}
          </Box>

          {/* Current Time Indicator */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${(currentTime / videoInfo.duration) * 100}%`,
              width: 2,
              bgcolor: 'red',
              zIndex: 3,
            }}
          />

          {/* Trim Segment */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${(trimSegment.start / videoInfo.duration) * 100}%`,
              width: `${(trimSegment.duration / videoInfo.duration) * 100}%`,
              bgcolor: 'rgba(25, 118, 210, 0.3)',
              border: '2px solid #1976d2',
              zIndex: 2,
            }}
          />

          {/* Start Handle */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${(trimSegment.start / videoInfo.duration) * 100}%`,
              width: 8,
              bgcolor: '#1976d2',
              cursor: 'ew-resize',
              zIndex: 4,
              '&:hover': { bgcolor: '#1565c0' },
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              handleTimelineMouseDown(e, 'start');
            }}
          />

          {/* End Handle */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${(trimSegment.end / videoInfo.duration) * 100}%`,
              width: 8,
              bgcolor: '#1976d2',
              cursor: 'ew-resize',
              zIndex: 4,
              '&:hover': { bgcolor: '#1565c0' },
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              handleTimelineMouseDown(e, 'end');
            }}
          />
        </Box>

        {/* Time Labels */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="textSecondary">
            {formatTime(trimSegment.start)}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {formatTime(trimSegment.end)}
          </Typography>
        </Box>

        {/* Progress */}
        {isProcessing && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              {status}
            </Typography>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Hidden canvas for frame generation */}
      <canvas
        ref={canvasRef}
        width={160}
        height={90}
        style={{ display: 'none' }}
      />
    </Box>
  );
};

export default MobileVideoEditor;

